import { IntlMessageFormat } from 'intl-messageformat';
import { useI18n } from './I18nProvider.js';

const defaultLocale = 'en-US';
function getMessageInLocale(messages, id, locale) {
  if (messages[locale] && messages[locale][id]) {
    return messages[locale][id];
  }
  const language = locale.split('-')[0];
  if (messages[language] && messages[language][id]) {
    return messages[language][id];
  }
  for (const key of Object.keys(messages)) {
    if (key.startsWith(language + '-') && messages[key][id]) {
      return messages[key][id];
    }
  }
  return messages[defaultLocale][id];
}

/**
 * Creates a message formatter, which can be used to internationalize text from a message definition.
 * See the formatjs docs for more information on how to define messages that include plurals and more
 * advanced features.
 *
 * @see https://formatjs.io/docs/intl-messageformat/
 */
function createMessageFormatter(messages, locale) {
  const formatMessage = (id, values) => {
    const msg = new IntlMessageFormat(getMessageInLocale(messages, id, locale), locale);
    const res = msg.format(values);
    if (Array.isArray(res)) {
      return res.join('');
    }
    return res;
  };
  const formatReactMessage = (id, values) => {
    const msg = new IntlMessageFormat(getMessageInLocale(messages, id, locale), locale);

    // When getting the list of message chunks from a user provided
    // function (probably a React component), IntlMessageFormat
    // doesn't give the caller an index to key their elements by.
    // We track our own with this.
    const entriesWithIndexProxy = Object.entries(values || {}).map(x => {
      const [key, value] = x;

      // Skip non-function entries.
      if (typeof value !== 'function') {
        return x;
      }

      // This index has a range related to just the
      // count of chunks in the one message (i.e.,
      // this isn't a global that goes up forever)
      let i = 0;
      const next = p => {
        return value(p, ++i);
      };
      return [key, next];
    });
    const indexedValues = Object.fromEntries(entriesWithIndexProxy);
    return msg.format(indexedValues);
  };
  return {
    formatMessage,
    formatReactMessage
  };
}

/**
 * Using the current locale context:
 * Create a message formatter, which can be used to internationalize text from a message definition.
 * See the formatjs docs for more information on how to define messages that include plurals and more
 * advanced features.
 *
 * @see https://formatjs.io/docs/intl-messageformat/
 */
function useMessageFormatter(messages) {
  const {
    locale
  } = useI18n();
  return createMessageFormatter(messages, locale);
}

/**
 * Combine multiple message definitions into a single one for usage in the message formatter.
 */
function combineMessages(message1, message2, message3, message4) {
  // Why not have a params arg? TypeScript can't seem to infer the various types such
  // that they can be intersected for the return type. Four message sources seems like enough.
  const messages = [message1, message2, message3, message4].filter(x => x);
  const target = {};
  for (const msgDef of messages) {
    const locales = Object.keys(msgDef);
    for (const locale of locales) {
      if (!target[locale]) {
        target[locale] = {};
      }
      const entries = msgDef[locale];
      Object.assign(target[locale], entries);
    }
  }
  return target;
}

export { combineMessages, createMessageFormatter, getMessageInLocale, useMessageFormatter };
